// v232 admin page for Cross-Instance Geo-Sync (item 48 of
// PRODUCT-ROADMAP.md, "Cross-Instance Geo-Sync"; item 59 in the
// recommended delivery order). Phases A + B + C.
//
// Four cards top to bottom:
//   1. Master settings — enable toggle, instance ID, push interval,
//      outbox retention.
//   2. Peers — table of remote instances + add/edit form (name,
//      endpoint, token, direction, enabled, plus Phase B
//      bandwidth_kbps / pull_interval_sec / pull_batch_size knobs).
//      Per-row actions: Test connection / Pause-Resume (cursor-
//      preserving toggle, Phase C) / Flip direction (push <-> pull
//      one-click failover, Phase C) / Edit / Delete.
//   3. Recent applies — Phase B tail of geosync_apply_audit so the
//      operator can confirm inbound replication is landing (or, when
//      it isn't, see the per-event reason: skipped, conflict, loop,
//      error).
//   4. Recent outbox — tail of geosync_outbox so an operator can
//      confirm replication events are being enqueued by local call
//      sites (Phase C wires the project / repo / artifact /
//      security-block upserts into geosync.Emit so this card finally
//      fills up on a default OrbitalReg installation with the master
//      toggle ON).

import { useEffect, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowLeftRight,
  CheckCircle2,
  Globe,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Trash2,
  Zap,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  useGeoSettings,
  useGeoPeers,
  useGeoOutbox,
  useGeoApplyAudit,
  updateGeoSettings,
  createGeoPeer,
  updateGeoPeer,
  deleteGeoPeer,
  testGeoPeer,
  pauseGeoPeer,
  resumeGeoPeer,
  flipGeoPeerDirection,
  type GeoApplyAuditRow,
  type GeoSettings,
  type GeoPeer,
  type GeoPeerInput,
  type GeoOutboxRow,
} from '../../api/geo';

const LABEL_CLASS = 'text-sm text-fg-muted block mb-1.5';
const SELECT_CLASS =
  'w-full px-3 py-2 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function fmtRel(d?: string | null): string {
  if (!d) return 'never';
  const t = new Date(d).getTime();
  const diff = Date.now() - t;
  if (Number.isNaN(diff)) return d;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function fmtBytes(n: number): string {
  if (!n || n < 1024) return `${n} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function PeerStatusCell({ p }: { p: GeoPeer }) {
  if (!p.enabled) return <Badge tone="neutral">disabled</Badge>;
  if (!p.has_token) return <Badge tone="warning">no token</Badge>;
  if (p.consecutive_failures > 0 || p.consecutive_pull_failures > 0) {
    const title = [
      p.consecutive_failures > 0 ? `push: ${p.last_error}` : '',
      p.consecutive_pull_failures > 0 ? `pull: ${p.last_pull_error}` : '',
    ]
      .filter(Boolean)
      .join('\n');
    return (
      <span className="text-danger inline-flex items-center gap-1 text-xs" title={title}>
        <AlertTriangle className="h-3.5 w-3.5" />
        failing ({p.consecutive_failures}p / {p.consecutive_pull_failures}u)
      </span>
    );
  }
  return (
    <span className="text-success inline-flex items-center gap-1 text-xs">
      <CheckCircle2 className="h-3.5 w-3.5" />
      ok
    </span>
  );
}

function emptyPeerDraft(): GeoPeerInput {
  return {
    name: '',
    endpoint_url: '',
    bearer_token: '',
    direction: 'push',
    enabled: false,
    bandwidth_kbps: 0,
    pull_interval_sec: 60,
    pull_batch_size: 100,
  };
}

const DECISION_TONE: Record<
  GeoApplyAuditRow['decision'],
  { tone: 'success' | 'warning' | 'danger' | 'neutral'; label: string }
> = {
  applied: { tone: 'success', label: 'applied' },
  skipped: { tone: 'neutral', label: 'skipped' },
  conflict: { tone: 'warning', label: 'conflict' },
  loop: { tone: 'neutral', label: 'loop' },
  error: { tone: 'danger', label: 'error' },
};

function GeoMasterSettingsCard({
  draftSettings,
  setDraftSettings,
  saveSettings,
  busy,
}: Readonly<{
  draftSettings: GeoSettings | null;
  setDraftSettings: (s: GeoSettings) => void;
  saveSettings: () => void;
  busy: boolean;
}>) {
  return (
    <Card className="mb-6">
      <CardHeader
        title="Master settings"
        description="Toggle the push + pull workers, set the instance ID written into outbound events (and used for inbound loop detection), and tune the push cadence + outbox retention window."
      />
      <CardBody>
        {draftSettings && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-fg-muted">
                  <input
                    type="checkbox"
                    checked={draftSettings.enabled}
                    onChange={(e) =>
                      setDraftSettings({ ...draftSettings, enabled: e.target.checked })
                    }
                  />
                  <span>
                    Enable geo-sync workers{' '}
                    <span className="text-xs text-fg-subtle ml-2">
                      (a local instance ID below is required when enabled)
                    </span>
                  </span>
                </label>
              </div>
              <div>
                <label className={LABEL_CLASS} htmlFor="geo-settings-instance-id">
                  Instance ID
                </label>
                <Input
                  id="geo-settings-instance-id"
                  value={draftSettings.instance_id}
                  onChange={(e) =>
                    setDraftSettings({ ...draftSettings, instance_id: e.target.value })
                  }
                  placeholder="e.g. orbitalreg-eu-west"
                />
                <p className="text-xs text-fg-muted mt-1.5">
                  Stamped onto every outbound event. Receiving peers compare against
                  this to drop self-originated echoes (decision=loop in the audit).
                </p>
              </div>
              <div>
                <label className={LABEL_CLASS} htmlFor="geo-settings-push-interval">
                  Push interval (seconds)
                </label>
                <Input
                  id="geo-settings-push-interval"
                  type="number"
                  min={5}
                  value={draftSettings.push_interval_sec}
                  onChange={(e) =>
                    setDraftSettings({
                      ...draftSettings,
                      push_interval_sec: Number.parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
              </div>
              <div>
                <label className={LABEL_CLASS} htmlFor="geo-settings-outbox-retention">
                  Outbox retention (days)
                </label>
                <Input
                  id="geo-settings-outbox-retention"
                  type="number"
                  min={0}
                  value={draftSettings.outbox_retention_days}
                  onChange={(e) =>
                    setDraftSettings({
                      ...draftSettings,
                      outbox_retention_days: Number.parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
                <p className="text-xs text-fg-muted mt-1.5">
                  Delivered-and-acked rows older than this drop. 0 disables reaping;
                  the table will grow unbounded under that mode.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="primary" onClick={saveSettings} disabled={busy}>
                Save settings
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function GeoPeersCard({
  peerRows,
  draftPeer,
  setDraftPeer,
  editingPeerID,
  savePeer,
  cancelEdit,
  busy,
  runTest,
  togglePeerEnabled,
  flipPeer,
  startEdit,
  removePeer,
}: Readonly<{
  peerRows: GeoPeer[];
  draftPeer: GeoPeerInput;
  setDraftPeer: (p: GeoPeerInput) => void;
  editingPeerID: string | null;
  savePeer: () => void;
  cancelEdit: () => void;
  busy: boolean;
  runTest: (p: GeoPeer) => void;
  togglePeerEnabled: (p: GeoPeer) => void;
  flipPeer: (p: GeoPeer) => void;
  startEdit: (p: GeoPeer) => void;
  removePeer: (p: GeoPeer) => void;
}>) {
  return (
    <Card className="mb-6">
      <CardHeader
        title={`Peers (${peerRows.length})`}
        description="Remote OrbitalReg instances we replicate to (push) or fetch from (pull). Add the peer's URL and an org_admin token minted on that side; flip Enabled once the Test connection check passes. Bandwidth defaults to 0 (uncapped) — set a per-peer kbps cap on cross-WAN deployments."
      />
      <CardBody>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 rounded-md border border-border/50 bg-bg-inset/30">
          <div>
            <label className={LABEL_CLASS} htmlFor="geo-peer-name">
              Name
            </label>
            <Input
              id="geo-peer-name"
              value={draftPeer.name}
              onChange={(e) => setDraftPeer({ ...draftPeer, name: e.target.value })}
              placeholder="e.g. dr-east"
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="geo-peer-endpoint-url">
              Endpoint URL
            </label>
            <Input
              id="geo-peer-endpoint-url"
              value={draftPeer.endpoint_url}
              onChange={(e) =>
                setDraftPeer({ ...draftPeer, endpoint_url: e.target.value })
              }
              placeholder="https://orbitalreg-east.example.com"
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="geo-peer-bearer-token">
              Bearer token{' '}
              {editingPeerID && (
                <span className="text-xs text-fg-subtle">
                  (leave blank to keep existing)
                </span>
              )}
            </label>
            <Input
              id="geo-peer-bearer-token"
              type="password"
              value={draftPeer.bearer_token ?? ''}
              onChange={(e) =>
                setDraftPeer({ ...draftPeer, bearer_token: e.target.value })
              }
              placeholder="orbr_…"
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="geo-peer-direction">
              Direction
            </label>
            <select
              id="geo-peer-direction"
              className={SELECT_CLASS}
              value={draftPeer.direction}
              onChange={(e) =>
                setDraftPeer({
                  ...draftPeer,
                  direction: e.target.value as GeoPeerInput['direction'],
                })
              }
            >
              <option value="push">push (we send)</option>
              <option value="pull">pull (we fetch)</option>
              <option value="bidir">bidirectional (push + pull)</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="geo-peer-bandwidth-kbps">
              Bandwidth cap (kbps)
            </label>
            <Input
              id="geo-peer-bandwidth-kbps"
              type="number"
              min={0}
              value={draftPeer.bandwidth_kbps}
              onChange={(e) =>
                setDraftPeer({
                  ...draftPeer,
                  bandwidth_kbps: Number.parseInt(e.target.value, 10) || 0,
                })
              }
            />
            <p className="text-xs text-fg-muted mt-1.5">
              0 = uncapped (default). Token-bucket gate on push body bytes + pull
              response bytes. Useful on cross-WAN replicas where you want to keep
              a backfill from saturating the uplink.
            </p>
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="geo-peer-pull-interval">
              Pull interval (seconds)
            </label>
            <Input
              id="geo-peer-pull-interval"
              type="number"
              min={5}
              value={draftPeer.pull_interval_sec}
              onChange={(e) =>
                setDraftPeer({
                  ...draftPeer,
                  pull_interval_sec: Number.parseInt(e.target.value, 10) || 60,
                })
              }
            />
            <p className="text-xs text-fg-muted mt-1.5">
              Only used for direction=pull or bidir. Default 60s sits well above the
              push cadence so pull is the gentler side.
            </p>
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="geo-peer-pull-batch-size">
              Pull batch size
            </label>
            <Input
              id="geo-peer-pull-batch-size"
              type="number"
              min={1}
              max={1000}
              value={draftPeer.pull_batch_size}
              onChange={(e) =>
                setDraftPeer({
                  ...draftPeer,
                  pull_batch_size: Number.parseInt(e.target.value, 10) || 100,
                })
              }
            />
            <p className="text-xs text-fg-muted mt-1.5">
              Max events per pull tick. 100 matches the push batch.
            </p>
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm text-fg-muted">
              <input
                type="checkbox"
                checked={draftPeer.enabled}
                onChange={(e) =>
                  setDraftPeer({ ...draftPeer, enabled: e.target.checked })
                }
              />
              <span>Enabled (cursor advances; otherwise the peer is dormant)</span>
            </label>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <Button variant="primary" onClick={savePeer} disabled={busy}>
              <Plus className="h-4 w-4 mr-1" />
              {editingPeerID ? 'Save changes' : 'Add peer'}
            </Button>
            {editingPeerID && (
              <Button variant="secondary" onClick={cancelEdit} disabled={busy}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        {peerRows.length === 0 ? (
          <p className="text-sm text-fg-muted">
            No peers configured yet. Add one above to start replicating.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Endpoint</th>
                  <th className="py-2 pr-4">Direction</th>
                  <th className="py-2 pr-4">Push cursor</th>
                  <th className="py-2 pr-4">Pull cursor</th>
                  <th className="py-2 pr-4">Bandwidth</th>
                  <th className="py-2 pr-4">Bytes (push / pull)</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {peerRows.map((p) => (
                  <tr key={p.id} className="border-b border-border/50">
                    <td className="py-2 pr-4 font-mono">{p.name}</td>
                    <td className="py-2 pr-4 text-xs text-fg-muted break-all max-w-[200px]">
                      {p.endpoint_url}
                    </td>
                    <td className="py-2 pr-4">
                      <Badge tone="neutral">{p.direction}</Badge>
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {p.last_pushed_id}
                      <div className="text-fg-muted">
                        {fmtRel(p.last_pushed_at)}
                      </div>
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {p.direction === 'push' ? (
                        <span className="text-fg-subtle">—</span>
                      ) : (
                        <>
                          {p.last_pulled_id}
                          <div className="text-fg-muted">
                            {fmtRel(p.last_pulled_at)}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-xs">
                      {p.bandwidth_kbps === 0 ? (
                        <span className="text-fg-muted">uncapped</span>
                      ) : (
                        <span className="font-mono">{p.bandwidth_kbps} kbps</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-xs font-mono text-fg-muted">
                      {fmtBytes(p.bytes_pushed)} / {fmtBytes(p.bytes_pulled)}
                    </td>
                    <td className="py-2 pr-4">
                      <PeerStatusCell p={p} />
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-bg-hover"
                          title="Test connection (HEAD /api/v1/geo/ingest)"
                          onClick={() => runTest(p)}
                        >
                          <Zap className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-bg-hover"
                          title={
                            p.enabled
                              ? 'Pause replication (cursor preserved)'
                              : 'Resume replication'
                          }
                          onClick={() => togglePeerEnabled(p)}
                          disabled={busy}
                        >
                          {p.enabled ? (
                            <Pause className="h-3.5 w-3.5" />
                          ) : (
                            <Play className="h-3.5 w-3.5 text-success" />
                          )}
                        </button>
                        {p.direction !== 'bidir' && (
                          <button
                            type="button"
                            className="p-1 rounded hover:bg-bg-hover"
                            title={`Flip direction (${p.direction} → ${
                              p.direction === 'push' ? 'pull' : 'push'
                            })`}
                            onClick={() => flipPeer(p)}
                            disabled={busy}
                          >
                            <ArrowLeftRight className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="text-xs underline px-1"
                          onClick={() => startEdit(p)}
                        >
                          edit
                        </button>
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-bg-hover text-danger"
                          title="Delete peer"
                          onClick={() => removePeer(p)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function GeoRecentAppliesCard({
  auditRows,
  onRefresh,
}: Readonly<{
  auditRows: GeoApplyAuditRow[];
  onRefresh: () => void;
}>) {
  return (
    <Card className="mb-6">
      <CardHeader
        title="Recent applies"
        description="Per-event decisions from the receive side: applied (the local row was created or updated), skipped (no-op upsert), conflict (the local row was newer than the incoming source_updated_at — last-write-wins kept the local copy), loop (we dropped a self-originated echo), error (the event payload was malformed or the upsert failed)."
        actions={
          <Button variant="secondary" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        }
      />
      <CardBody>
        {auditRows.length === 0 ? (
          <div className="text-sm text-fg-muted flex items-start gap-2">
            <Activity className="h-4 w-4 mt-0.5" />
            <span>
              No inbound events yet. Once a remote peer pushes to us (or we pull
              from a peer with direction=pull|bidir), the per-event decision shows
              up here.
            </span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Direction</th>
                  <th className="py-2 pr-4">Source</th>
                  <th className="py-2 pr-4">Event</th>
                  <th className="py-2 pr-4">Scope</th>
                  <th className="py-2 pr-4">Decision</th>
                  <th className="py-2 pr-4">Reason</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map((row) => {
                  const tone = DECISION_TONE[row.decision] ?? {
                    tone: 'neutral' as const,
                    label: row.decision,
                  };
                  return (
                    <tr key={row.id} className="border-b border-border/50">
                      <td className="py-2 pr-4 text-xs text-fg-muted">
                        {fmtRel(row.applied_at)}
                      </td>
                      <td className="py-2 pr-4 text-xs">
                        <Badge tone="neutral">
                          {row.direction === 'inbound_push' ? 'push' : 'pull'}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {row.source_instance || (
                          <span className="text-fg-subtle">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {row.event_type}
                        <span className="text-fg-subtle ml-1">#{row.event_id}</span>
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs text-fg-muted break-all max-w-[200px]">
                        {row.scope_id}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge tone={tone.tone}>{tone.label}</Badge>
                      </td>
                      <td className="py-2 pr-4 text-xs text-fg-muted truncate max-w-[280px]">
                        {row.reason}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function GeoRecentOutboxCard({
  outboxRows,
  onRefresh,
}: Readonly<{
  outboxRows: GeoOutboxRow[];
  onRefresh: () => void;
}>) {
  return (
    <Card>
      <CardHeader
        title="Recent outbox"
        description="Tail of geosync_outbox — the events queued for delivery to every peer. Phase C wires the project / repo / artifact / security-block upsert call sites into geosync.Emit, so this card fills as upserts happen on the local instance with the master toggle ON. Artifact bytes themselves are not replicated; the wire payload carries sha256 so a future phase can fetch lazily on first miss."
        actions={
          <Button variant="secondary" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        }
      />
      <CardBody>
        {outboxRows.length === 0 ? (
          <div className="text-sm text-fg-muted flex items-start gap-2">
            <Activity className="h-4 w-4 mt-0.5" />
            <span>
              The outbox is empty. Replication events are enqueued by the
              originating call sites only when the master toggle is ON — flip it
              in the Master settings card and create / edit a project, repo,
              artifact, or security block to confirm events show up here.
            </span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="py-2 pr-4">ID</th>
                  <th className="py-2 pr-4">Event</th>
                  <th className="py-2 pr-4">Scope</th>
                  <th className="py-2 pr-4">When</th>
                </tr>
              </thead>
              <tbody>
                {outboxRows.map((row) => (
                  <tr key={row.id} className="border-b border-border/50">
                    <td className="py-2 pr-4 font-mono text-xs">{row.id}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{row.event_type}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-fg-muted break-all max-w-[260px]">
                      {row.scope_id}
                    </td>
                    <td className="py-2 pr-4 text-xs text-fg-muted">
                      {fmtRel(row.occurred_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export function AdminGeo() {
  const settings = useGeoSettings();
  const peers = useGeoPeers();
  const outbox = useGeoOutbox();
  const applyAudit = useGeoApplyAudit();

  const [draftSettings, setDraftSettings] = useState<GeoSettings | null>(null);
  const [draftPeer, setDraftPeer] = useState<GeoPeerInput>(emptyPeerDraft);
  const [editingPeerID, setEditingPeerID] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data && !draftSettings) {
      setDraftSettings(settings.data);
    }
  }, [settings.data, draftSettings]);

  const peerRows = peers.data?.rows ?? [];
  const outboxRows = outbox.data?.rows ?? [];
  const auditRows = applyAudit.data?.rows ?? [];

  async function saveSettings() {
    if (!draftSettings) return;
    setBusy(true);
    setErrorMsg(null);
    setOkMsg(null);
    try {
      await updateGeoSettings({
        enabled: draftSettings.enabled,
        instance_id: draftSettings.instance_id,
        outbox_retention_days: draftSettings.outbox_retention_days,
        push_interval_sec: draftSettings.push_interval_sec,
      });
      setOkMsg('Settings saved.');
      await settings.mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(p: GeoPeer) {
    setEditingPeerID(p.id);
    setDraftPeer({
      name: p.name,
      endpoint_url: p.endpoint_url,
      bearer_token: '',
      direction: p.direction,
      enabled: p.enabled,
      bandwidth_kbps: p.bandwidth_kbps,
      pull_interval_sec: p.pull_interval_sec,
      pull_batch_size: p.pull_batch_size,
    });
  }

  function cancelEdit() {
    setEditingPeerID(null);
    setDraftPeer(emptyPeerDraft());
  }

  async function savePeer() {
    setBusy(true);
    setErrorMsg(null);
    setOkMsg(null);
    try {
      if (editingPeerID) {
        await updateGeoPeer(editingPeerID, draftPeer);
        setOkMsg('Peer updated.');
      } else {
        await createGeoPeer(draftPeer);
        setOkMsg('Peer added.');
      }
      cancelEdit();
      await peers.mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function removePeer(p: GeoPeer) {
    if (
      !confirm(
        `Delete peer "${p.name}"? Its replication cursor will be dropped; re-creating the peer later will start the cursor at 0 and replay the entire outbox.`,
      )
    ) {
      return;
    }
    try {
      await deleteGeoPeer(p.id);
      await peers.mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  }

  async function runTest(p: GeoPeer) {
    setBusy(true);
    setErrorMsg(null);
    setOkMsg(null);
    try {
      const res = await testGeoPeer(p.id);
      if (res.ok) {
        setOkMsg(`Peer "${p.name}" reachable: ${res.status}`);
      } else {
        setErrorMsg(`Peer "${p.name}" unreachable: ${res.error ?? 'unknown'}`);
      }
      await peers.mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Phase C — pause / resume preserve the cursor (geosync_peer_state.
  // last_pushed_id and last_pulled_id stay intact across the toggle)
  // so a maintenance-window pause + resume doesn't replay the entire
  // outbox on the next tick.
  async function togglePeerEnabled(p: GeoPeer) {
    setBusy(true);
    setErrorMsg(null);
    setOkMsg(null);
    try {
      if (p.enabled) {
        await pauseGeoPeer(p.id);
        setOkMsg(`Peer "${p.name}" paused. Cursor preserved.`);
      } else {
        await resumeGeoPeer(p.id);
        setOkMsg(`Peer "${p.name}" resumed.`);
      }
      await peers.mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Phase C failover-switch — promote a passive replica or demote a
  // failed primary in one click. push <-> pull; bidir is rejected by
  // the backend with a 400 because there's no meaningful flip.
  async function flipPeer(p: GeoPeer) {
    if (p.direction === 'bidir') {
      setErrorMsg(
        `Peer "${p.name}" is bidirectional; flip is undefined. Edit the row to switch to push or pull.`,
      );
      return;
    }
    if (
      !confirm(
        `Flip peer "${p.name}" from ${p.direction} to ${
          p.direction === 'push' ? 'pull' : 'push'
        }? Cursor state is preserved across the flip.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setErrorMsg(null);
    setOkMsg(null);
    try {
      const res = await flipGeoPeerDirection(p.id);
      setOkMsg(`Peer "${p.name}" is now ${res.direction}.`);
      await peers.mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminShell
      title={
        <span className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-nebula" />
          Geo-sync
        </span>
      }
      description="Cross-instance replication of project / repo / artifact-row / security-block metadata to one or more remote OrbitalReg instances. Phase A: schema + push worker + admin surface. Phase B: pull worker + last-write-wins conflict resolution + per-peer bandwidth throttling. Disabled by default; the workers short-circuit until you enable the master toggle below."
    >
      {settings.error && (
        <Card className="mb-4 border-danger/40">
          <CardBody>
            <p className="text-sm text-danger">
              Failed to load settings: {String(settings.error)}
            </p>
          </CardBody>
        </Card>
      )}
      {(errorMsg || okMsg) && (
        <Card className={errorMsg ? 'mb-4 border-danger/40' : 'mb-4 border-success/40'}>
          <CardBody>
            <p className={errorMsg ? 'text-sm text-danger' : 'text-sm text-success'}>
              {errorMsg || okMsg}
            </p>
          </CardBody>
        </Card>
      )}

      <GeoMasterSettingsCard
        draftSettings={draftSettings}
        setDraftSettings={setDraftSettings}
        saveSettings={saveSettings}
        busy={busy}
      />

      <GeoPeersCard
        peerRows={peerRows}
        draftPeer={draftPeer}
        setDraftPeer={setDraftPeer}
        editingPeerID={editingPeerID}
        savePeer={savePeer}
        cancelEdit={cancelEdit}
        busy={busy}
        runTest={runTest}
        togglePeerEnabled={togglePeerEnabled}
        flipPeer={flipPeer}
        startEdit={startEdit}
        removePeer={removePeer}
      />

      <GeoRecentAppliesCard auditRows={auditRows} onRefresh={() => applyAudit.mutate()} />

      <GeoRecentOutboxCard outboxRows={outboxRows} onRefresh={() => outbox.mutate()} />
    </AdminShell>
  );
}
